import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ANALYTICS_ERROR_TYPE, ANALYTICS_PAGE_TYPE } from './analytics.enum';
import { SessionService } from './session.service';
import { IdentificationService } from '../feature/universal-identification/services/identification.service';
import { environment } from '@/environment';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

/* eslint-disable no-unused-vars */
declare global {
  interface Window {
    dataLayer: any[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private isBrowser: any = isPlatformBrowser(inject(PLATFORM_ID));
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private identificationService = inject(IdentificationService);

  public pageIndex?: number | null = null;

  addDataLayer(event: string, dataLayerObject: any): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      ...dataLayerObject,
      event,
    });
  }

  public trackPageView(strapiUID: string, pageType = 'care|faults|dashboard') {
    if (this.isBrowser) {
      const ses = this.sessionService.getData();
      if (!sessionStorage.getItem('vmo2_zeus_app_referrer')) {
        sessionStorage.setItem('vmo2_zeus_app_referrer', window.document.referrer);
      }
      if (
        this.pageIndex ||
        !sessionStorage?.getItem('vmo2_zeus_previous_url') ||
        this.getWindowLocationHref() !== sessionStorage?.getItem('vmo2_zeus_previous_url')
      ) {
        const customData = this.getCustomData(ses);
        const pageViewData = this.getPageViewData(ses, strapiUID, pageType);

        const trackingData = {
          ...pageViewData,
          ...customData,
        };
        this.addDataLayer('pageview', trackingData);
      }
      sessionStorage.setItem('vmo2_zeus_previous_url', this.getWindowLocationHref());
      this.pageIndex = null;
    }
  }

  getIdentityClaimProperty(name: string): string {
    const claims = this.getIdentityClaims();
    if (!claims) {
      return null;
    }
    /* eslint-disable @typescript-eslint/dot-notation */
    return claims[name];
  }

  getIdentityClaims() {
    const claims = sessionStorage.getItem('id_token_claims_obj');
    if (!claims) {
      return null;
    }
    return JSON.parse(claims);
  }

  public getCustomData(ses) {
    const customData: any = {};
    const cableCustomerId = this.getIdentityClaimProperty('vmHouseholdID');
    let softIdentMethodValue = 0;

    // TODO: if TID then
    //  softIdentMethodValue =  5
    if (cableCustomerId) {
      softIdentMethodValue = 3;
    } else if (ses?.id?.account?.householdId) {
      softIdentMethodValue = 1;
    }

    customData.user = this.getUserData(
      sessionStorage.getItem(this.identificationService.getAuthTokenName()),
      ses?.id?.account?.householdId,
      softIdentMethodValue,
      ses?.id?.account?.accountStatus,
      null, // tid
      cableCustomerId
    );

    if (ses?.ss?.outages || ses?.dc?.diagnosticId) {
      let einstein_action_code_1 = ses?.dc?.check
        ? this.sessionService.getDiagnosticActionCode(ses?.dc?.check[0])
        : null;
      let einstein_action_code_2 = null;

      if (ses?.dc?.check?.length > 1) {
        einstein_action_code_1 = this.sessionService.getDiagnosticActionCode(ses?.dc?.check[1]);
        einstein_action_code_2 = this.sessionService.getDiagnosticActionCode(ses?.dc?.check[0]);
      }

      const appointment = this.sessionService.getNextAppointment();

      customData.faults = this.getFaultsData(
        ses?.id?.account?.accountStatus,
        ses?.ss?.outages?.length > 0 ? 'TRUE' : 'FALSE',
        appointment?.workOrderQuotaCodeDescription,
        ses?.dc?.diagnosticId,
        einstein_action_code_1,
        einstein_action_code_2
      );
    }
    return customData;
  }

  public getPageViewData(ses: any, strapiUID: string, pageType?: string) {
    const cleanHostname = location?.hostname?.replace('www.', '');
    const subDomainRegex = /^([\w-]+)(?:\.[\w-]+){2,}$/;
    const subdomain = subDomainRegex.test(cleanHostname) ? cleanHostname.match(subDomainRegex)[1] : '';

    if (
      !this.pageIndex &&
      (this.router.url?.toLocaleLowerCase()?.endsWith('-advice') ||
        this.router.url?.toLocaleLowerCase()?.endsWith('/advice') ||
        this.router.url?.toLocaleLowerCase()?.endsWith('/advices'))
    ) {
      this.pageIndex = 1;
    }

    return {
      page: {
        currentTimestamp: Date.now(),
        embeddedWebApp: ses?.loc?.isEmbeddedWebApp?.toString(),
        environment: environment.name === 'production' ? 'prod' : 'dev',
        pageReferrer: ses?.loc?.referrer,
        pageUrl: this.getWindowLocationHref(),
        pageID: strapiUID,
        pageType: pageType || ANALYTICS_PAGE_TYPE[ses?.loc?.pathname] || ANALYTICS_PAGE_TYPE.DEFAULT,
        path: this.router.url + (this.pageIndex ? `-${this.pageIndex}` : ''),
        platform: 'AngularWeb',
        query: ses?.loc?.queryString,
        sessionReferrer: sessionStorage?.getItem('vmo2_zeus_app_referrer'),
        subdomain,
        pagePath: ses?.loc?.pathname,
        pageModDate: null,
        journey: 'care faults dashboard',
        pink: 'Non Pink',
      },
    };
  }

  public getUserData(
    conversationId?: string,
    softcableCustomerID?: string,
    softIdentMethod?: number,
    accountStatus?: string,
    tid?: string,
    cableCustomerId?: string
  ) {
    return {
      conversationId,
      lastVisit: 'undefined',
      hashedEmail: 'undefined',
      cable: {
        softcableCustomerID,
        softIdentMethod,
        curHolding: 'undefined',
        curEOC: 'undefined',
        existingOffers: 'undefined',
        recommendations: 'undefined',
        accountStatus,
        tid,
        cableCustomerId,
      },
    };
  }

  public getFaultsData(
    accountStatus?: string,
    areaFault?: string,
    description?: string,
    diagnosticIdentifier?: string,
    einsteinActionCode1?: string,
    einsteinActionCode2?: string
  ) {
    return {
      accountStatus: accountStatus?.toUpperCase(),
      areaFault,
      description,
      diagnosticIdentifier,
      einsteinActionCode1,
      einsteinActionCode2,
    };
  }

  public trackFaultCheckEvent(): void {
    const ses = this.sessionService.getData();
    const appointment = this.sessionService.getNextAppointment();

    const dcCheck_0 = ses?.dc?.check?.length ? ses?.dc?.check[0] : null;
    const dcCheckConnectedDevicesInformation_0 = dcCheck_0?.connectedDevicesInformation?.length
      ? dcCheck_0?.connectedDevicesInformation[0]
      : null;

    let einstein_action_code_1 = dcCheck_0 ? this.sessionService.getDiagnosticActionCode(dcCheck_0) : null;
    let einstein_action_code_2 = null;

    if (ses?.dc?.check?.length > 1) {
      einstein_action_code_1 = this.sessionService.getDiagnosticActionCode(ses?.dc?.check[1]);
      einstein_action_code_2 = this.sessionService.getDiagnosticActionCode(dcCheck_0);
    }

    const authnLevel = this.getIdentityClaimProperty('authnLevel');
    const is_authenticated = authnLevel === 'pwd';

    this.addDataLayer('faults_check', {
      event: 'faults_check',
      event_name: 'faults_check',
      event_params: {
        account_status: ses?.id?.account?.accountStatus,
        area_fault: ses?.ss?.outages?.length > 0 ? 'TRUE' : 'FALSE',
        description: appointment?.workOrderQuotaCodeDescription,
        diagnostic_identifier: ses?.dc?.diagnosticId,
        einstein_action_code_1: einstein_action_code_1,
        einstein_action_code_2: einstein_action_code_2,
        access_point_data_exists: null, // ses?.angularPublicSession?.accessPointActionCodes ? ses?.angularPublicSession?.accessPointActionCodes?.length ? 'TRUE' : 'FALSE' : null,
        access_point_error_count: null, // ses?.angularPublicSession?.accessPointActionCodes?.length,
        hub_settings_changed: dcCheck_0?.equipmentSettings?.hasHubSettingsInformation || null,
        client_device_data_exist: dcCheckConnectedDevicesInformation_0?.hasConnectedDevicesInformation || null,
        client_device_count: dcCheckConnectedDevicesInformation_0?.numberOfCustomerDevices || null,
        client_device_error_count: dcCheckConnectedDevicesInformation_0?.numberOfDevicesWithIssues || null,
        dominant_device_issue: dcCheckConnectedDevicesInformation_0?.dominantConnectedDeviceIssue || null,
        is_authenticated,
        wo_order_number: null,
        postcode: this.getCleanPostcode(ses?.id?.account?.postcode),
        premise_uid: ses?.id?.account?.householdId,
      },
    });
  }

  public trackSiteErrorEvent(errorType: ANALYTICS_ERROR_TYPE, errorCode?: number, errorMessage?: string): void {
    this.addDataLayer('site_error', {
      event: 'site_error',
      /* eslint-disable */
      event_name: 'site_error',
      event_params: {
        error_type: errorType,
        error_code: errorCode,
        error_message: errorMessage,
        /* eslint-enable */
      },
    });
  }

  public trackBroadbandCompensationEvent(
    postcode?: string,
    premiseUID?: string,
    outageID?: string,
    outageAlertSubscribed?: string
  ): void {
    this.addDataLayer('broadband_compensation', {
      event: 'broadband_compensation',
      /* eslint-disable */
      event_name: 'broadband_compensation_registration',
      event_params: {
        postcode: this.getCleanPostcode(postcode),
        premise_uid: premiseUID,
        outage_id: outageID,
        outage_alert_subscribed: outageAlertSubscribed,
        /* eslint-enable */
      },
    });
  }

  public getCleanPostcode(postcode: string): string {
    if (postcode) {
      const postcodeRegEx = /^[A-Z0-9]{2,4}(?=(?:\s*[A-Z0-9]{3})?$)/gm;
      postcode = postcodeRegEx.test(postcode) ? postcode?.match(postcodeRegEx)[0] : '';
    }
    return postcode;
  }

  public trackCallModalViewEvent(): void {
    this.addDataLayer('call', {
      event: 'call',
      /* eslint-disable */
      event_name: 'call_support',
      event_params: {
        call_type: 'care - faults: general',
        /* eslint-enable */
      },
    });
  }

  addContentClickedToDataLayer(eventParams: object): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    window.dataLayer.push({
      event: 'content_actions',
      event_name: 'content_clicked',
      event_params: eventParams,
    });
  }

  private getWindowLocationHref() {
    return `${window?.location?.origin}${environment?.deployUrl}${this.router?.url}`;
  }

  public trackBookATechnicianSlotEvent(
    engineerBookingId?: string,
    installationDate?: string,
    installationSlot?: string,
    bookingType?: string
  ): void {
    this.addDataLayer('ecommerce', null);
    if (installationDate) {
      this.addDataLayer('book_faults_engineer', {
        event: 'book_faults_engineer',
        /* eslint-disable */
        event_name: bookingType == 'cancellation' ? 'cancel_engineer_slot' : 'book_engineer_slot',
        event_params: {
          engineer_booking_id: engineerBookingId,
          installation_type: 'faults',
          installation_date: installationDate,
          installation_slot: installationSlot,
          booking_type: bookingType, // 'cancellation' | 'new booking' | 'amend booking'
          /* eslint-enable */
        },
      });
    } else {
      this.addDataLayer('book_faults_engineer', {
        event: 'book_faults_engineer',
        /* eslint-disable */
        event_name: bookingType == 'cancellation' ? 'cancel_engineer_slot' : 'book_engineer_slot',
        event_params: {
          engineer_booking_id: engineerBookingId,
          installation_type: 'faults',
          booking_type: bookingType, // 'cancellation' | 'new booking' | 'amend booking'
          /* eslint-enable */
        },
      });
    }
  }

  public trackLiveChatEvent(liveChatEvent = 'live_chat_opened', livePersonId?: string): void {
    if (livePersonId) {
      this.addDataLayer('live_person', {
        event: 'live_person',
        eventAction: 'liveperson',
        eventCategory: 'site interaction',
        eventLabel: 'live-chat:opened',
        event_name: liveChatEvent,
        event_params: {
          live_person_id: livePersonId,
        },
      });
    } else {
      this.addDataLayer('live_person', {
        event: 'live_person',
        eventAction: 'liveperson',
        eventCategory: 'site interaction',
        eventLabel: 'live-chat:opened',
        event_name: liveChatEvent,
      });
    }
  }

  public trackCustomerFeedbackEvent(contentText?: string): void {
    if (contentText) {
      const feedbackTrigger = [
        "Yes, I'm back online",
        "Yes, it's working",
        'The TV box is working again',
        'No, I still need help',
        'Yes',
        'No',
      ];
      contentText = contentText?.replace('’', "'")?.trim();
      if (feedbackTrigger.indexOf(contentText) > -1) {
        this.addDataLayer('feedback', {
          event: 'feedback',
          /* eslint-disable */
          event_name: 'online_support_feedback',
          event_params: {
            support_category: 'faults',
            content_text: contentText,
            content_type: `web - faults - csat ${contentText?.toLocaleLowerCase()}`,
            /* eslint-enable */
          },
        });
      }
    }
  }

  public trackContentActionEvent(contentType: string, contentPosition: string, contentName: string): void {
    this.addDataLayer('content_actions', {
      event: 'content_actions',
      event_name: 'content_impression',
      event_params: {
        content_type: contentType, // 'web - faults - pod detail location',
        content_position: contentPosition, // '',
        content_name: contentName, // 'Pod Detail Location',
      },
    });
  }
}
